import { num } from './utilities.js'

/**
 * Sign → verbal comparison word for the kohandustabel.
 *
 * Mirrors `App\Services\Valuation\ValuationKeyMapping::getComparisonLabel()`
 * and `::toComparisonKey()`, which label the AI-computed adjustments on the
 * server. A manual `*_kohandus` edit never passes through the server mapping,
 * so the store applies the same rule here — keep the two in step (same
 * mirror convention as masterPivotFields.js ↔ PivotFieldRegistry.php).
 *
 * Convention: positive adjustment = comparable is worse than the subject
 * (Kehvem), negative = better (Parem), zero = equivalent (Samaväärne).
 */
export const COMPARISON_LABEL_WORSE = 'Kehvem'
export const COMPARISON_LABEL_BETTER = 'Parem'
export const COMPARISON_LABEL_EQUAL = 'Samaväärne'

/**
 * @param {number|string|null|undefined} value raw cell value ("-10", "2,5", 0)
 * @returns {string|null} the label, or null when the cell is empty/unparseable
 *   (`num('')` is 0, so emptiness is checked before coercion)
 */
export const comparisonLabelFor = value => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && value.trim() === '') return null

  const numeric = num(value)
  if (typeof numeric !== 'number' || !Number.isFinite(numeric)) return null

  if (numeric > 0) return COMPARISON_LABEL_WORSE
  if (numeric < 0) return COMPARISON_LABEL_BETTER
  return COMPARISON_LABEL_EQUAL
}

/**
 * The word a `*_kohandus` cell committed WITHOUT a change writes above it.
 *
 * Every adjustment starts at 0 with no word, so a valuer who leaves the 0 and
 * tabs on gets Samaväärne exactly as an edit to 0 gives it (helpdesk,
 * 2026-10-27). Only an empty word is filled: a typed word is never replaced.
 * Only a 0 fills: a non-zero adjustment always arrives with its word (manual
 * edit or AI), so an empty word beside one was cleared on purpose.
 *
 * @param {number|string|null|undefined} adjustmentValue the committed value
 * @param {string|null|undefined} currentWord the `*_vordlus` word above it
 * @returns {string|null} Samaväärne, or null to leave the word alone
 */
export const labelForUnchangedCommit = (adjustmentValue, currentWord) => {
  const wordIsEmpty =
    currentWord === null ||
    currentWord === undefined ||
    String(currentWord).trim() === ''
  if (!wordIsEmpty) return null

  return comparisonLabelFor(adjustmentValue) === COMPARISON_LABEL_EQUAL
    ? COMPARISON_LABEL_EQUAL
    : null
}

/**
 * `asukoha_kohandus` → `asukoha_vordlus`; null for any other key.
 *
 * @param {string} fieldKey
 * @returns {string|null}
 */
export const comparisonKeyFor = fieldKey =>
  typeof fieldKey === 'string' && fieldKey.endsWith('_kohandus')
    ? fieldKey.replace(/_kohandus$/, '_vordlus')
    : null
